import {
  BankIcon,
  ChartLineUpIcon,
  CoinsIcon,
  DownloadSimpleIcon,
  FileTextIcon,
  HouseIcon,
  IdentificationCardIcon,
  LifebuoyIcon,
  ListBulletsIcon,
  MegaphoneIcon,
  MoneyIcon,
  PercentIcon,
  ReceiptIcon,
  TrayIcon,
  UploadSimpleIcon,
  UserIcon,
  WalletIcon,
} from "@phosphor-icons/react";
import {
  Accordion,
  AccordionContent,
  AccordionTrigger,
  AccordionItem,
  Button,
  ScrollArea,
  Tooltip,
} from "@reactive-resume/ui";
import { useAdminQueues } from "@/client/services/notification";
import { useMyPermissions } from "@/client/services/permissions/me";
import { cn } from "@reactive-resume/utils";
import { Link, useLocation } from "react-router";

import { Icon } from "@/client/components/icon";
type Props = {
  className?: string;
};

/** Compteur de file d'attente affiché sur une entrée de navigation. */
type BadgeKey = "alerts" | "withdrawals" | "help" | "campaigns";

type SidebarItem = {
  path: string;
  name: string;
  icon?: React.ReactNode;
  menu: string;
  /** Permission RBAC requise. Absente = visible par tout compte interne. */
  permission?: string;
  /** File d'attente dont le compteur alimente la pastille. */
  badge?: BadgeKey;
  /** Une action humaine est en attente : pastille ambre au lieu du gris. */
  actionable?: boolean;
};

const menu = [
  "Général",
  "Utilisateurs",
  "Contenu",
  "Communication",
  "Finance",
  "Support",
  "Configuration",
  "Système",
];

const sidebarItems: SidebarItem[] = [
  {
    path: "/admin/dashboard/overview",
    name: "Vue d'ensemble",
    icon: <HouseIcon />,
    menu: "Général",
    permission: "dashboard.view",
  },
  // Boîte admin : flux d'alertes critiques (retraits, aide, paiements) de CET
  // opérateur. La pastille compte ses alertes non lues.
  {
    path: "/admin/dashboard/inbox",
    name: "Boîte admin",
    icon: <TrayIcon />,
    menu: "Général",
    badge: "alerts",
    actionable: true,
  },

  {
    path: "/admin/dashboard/client",
    name: "Clients",
    icon: <UserIcon />,
    menu: "Utilisateurs",
    permission: "user.view",
  },
  //{ path: "/admin/dashboard/ambassador", name: "Ambassadeurs", icon: <UsersIcon />, menu: "Utilisateurs" },
  {
    path: "/admin/dashboard/admin",
    name: "Utilisateurs interne",
    icon: <IdentificationCardIcon />,
    menu: "Utilisateurs",
    permission: "user.view",
  },

  {
    path: "/admin/dashboard/resume",
    name: "CV",
    icon: <FileTextIcon />,
    menu: "Contenu",
    permission: "resume.view",
  },
  {
    path: "/admin/dashboard/reference",
    name: "Références",
    icon: <ListBulletsIcon />,
    menu: "Contenu",
    permission: "profile.view",
  },
  {
    path: "/admin/dashboard/download",
    name: "Téléchargements",
    icon: <DownloadSimpleIcon />,
    menu: "Contenu",
    permission: "download.view",
  },

  {
    path: "/admin/dashboard/campaigns",
    name: "Campagnes",
    icon: <MegaphoneIcon />,
    menu: "Communication",
    permission: "campaign.view",
    badge: "campaigns",
  },
  {
    path: "/admin/dashboard/imports",
    name: "Imports & Activation",
    icon: <UploadSimpleIcon />,
    menu: "Communication",
    permission: "campaign.view",
  },

  {
    path: "/admin/dashboard/coupon",
    name: "Coupons",
    icon: <PercentIcon />,
    menu: "Finance",
    permission: "coupon.view",
  },
  {
    path: "/admin/dashboard/rate",
    name: "Tarifs",
    icon: <CoinsIcon />,
    menu: "Finance",
    permission: "rate.view",
  },
  {
    path: "/admin/dashboard/bonus",
    name: "Bonus",
    icon: <WalletIcon />,
    menu: "Finance",
    permission: "credit.view",
  },
  {
    path: "/admin/dashboard/withdrawal",
    name: "Retraits",
    icon: <MoneyIcon />,
    menu: "Finance",
    permission: "withdrawal.view",
    badge: "withdrawals",
    actionable: true,
  },
  {
    path: "/admin/dashboard/transaction",
    name: "Transactions",
    icon: <ChartLineUpIcon />,
    menu: "Finance",
    permission: "payment.view",
  },

  {
    path: "/admin/dashboard/help",
    name: "Centre d'aide",
    icon: <LifebuoyIcon />,
    menu: "Support",
    permission: "help.view",
    badge: "help",
    actionable: true,
  },

  {
    path: "/admin/dashboard/payment-provider",
    name: "Fournisseurs de paiement",
    icon: <BankIcon />,
    menu: "Configuration",
    permission: "payment.view",
  },
  {
    path: "/admin/dashboard/role-permission",
    name: "Contrôle d'accès",
    icon: <BankIcon />,
    menu: "Configuration",
    permission: "role.view",
  },

  {
    path: "/admin/dashboard/audit",
    name: "Journal d'audit",
    icon: <ReceiptIcon />,
    menu: "Système",
    permission: "audit.view",
  },
];

type SidebarItemProps = SidebarItem & { count?: number; onClick?: () => void };

const SidebarItem = ({ path, name, icon, count = 0, actionable, onClick }: SidebarItemProps) => {
  const isActive = useLocation().pathname === path;

  const element = (
    <Button
      asChild
      size="md"
      variant="sidebar-button" // Conservé si votre lib le supporte
      className={cn(
        "w-full justify-start",
        // Utilisation des tokens admin pour les états
        isActive
          ? "border-admin-border bg-admin-surface-elevated text-admin-text-primary"
          : "text-admin-text-secondary hover:bg-admin-surface-hover hover:text-admin-text-primary",
      )}
      onClick={onClick}
    >
      <Link to={path}>
        <div className="text-admin-icon-default mr-3">{icon}</div>
        <span className="flex-1 text-left">{name}</span>
        {count > 0 && (
          <span
            className={cn(
              "ml-auto grid min-w-5 place-items-center rounded-full px-1.5 py-0.5 text-[11px] font-semibold leading-none",
              actionable
                ? "bg-amber-500/15 text-amber-700 ring-1 ring-amber-500/30"
                : "bg-admin-surface-elevated text-admin-text-secondary",
            )}
          >
            {count > 99 ? "99+" : count}
          </span>
        )}
      </Link>
    </Button>
  );

  // Une file d'attente non vide mérite une explication au survol.
  if (count > 0 && actionable) {
    return <Tooltip content={`${count} élément(s) en attente de traitement`}>{element}</Tooltip>;
  }

  return element;
};

type SidebarProps = {
  setOpen?: (open: boolean) => void;
};

export const Sidebar = ({ setOpen }: SidebarProps) => {
  const location = useLocation();
  const { permissions } = useMyPermissions();
  const queues = useAdminQueues();

  /**
   * Moindre privilège côté UI : une entrée sans permission explicite reste
   * visible (comptes internes sans RBAC configuré) ; toute entrée portant une
   * permission n'apparaît que si elle est réellement détenue.
   */
  const visibleItems = sidebarItems.filter(
    (item) => !item.permission || !permissions || permissions.includes(item.permission),
  );

  /** Alimente la pastille depuis l'agrégat de files d'attente. */
  const badgeCount = (key?: BadgeKey): number => {
    const data = queues.data;
    if (!key || !data) return 0;
    switch (key) {
      case "alerts":
        return data.alerts.unreadCount;
      case "withdrawals":
        return data.withdrawals.pendingCount;
      case "help":
        return data.help.unansweredCount;
      case "campaigns":
        return data.campaigns.sendingCount;
      default:
        return 0;
    }
  };

  const activeGroup = sidebarItems.find((item) => location.pathname === item.path)?.menu;

  // Un groupe dont toutes les entrées sont masquées disparaît entièrement.
  const visibleMenus = menu.filter((menuItem) =>
    visibleItems.some((item) => item.menu === menuItem),
  );

  return (
    <div className="flex h-full flex-col gap-y-4 border-r border-admin-border bg-admin-surface p-4">
      <div className="flex justify-center py-2 lg:ml-0">
        <Icon size={25} className="mx-auto lg:block" />
      </div>

      <ScrollArea hideScrollbar={false} className="flex-1">
        <div className="flex flex-col gap-2">
          {visibleMenus.map((menuItem) => (
            <Accordion key={menuItem} type="single" collapsible defaultValue={activeGroup}>
              <AccordionItem value={menuItem} className="border-b-0">
                <AccordionTrigger className="px-2 text-xs font-semibold uppercase tracking-wider text-admin-text-muted hover:text-admin-text-primary">
                  {menuItem}
                </AccordionTrigger>
                <AccordionContent className="flex flex-col gap-2 pb-2">
                  {visibleItems
                    .filter((item) => item.menu === menuItem)
                    .map((item) => (
                      <SidebarItem
                        {...item}
                        path={item.path}
                        key={item.path}
                        count={badgeCount(item.badge)}
                        onClick={() => setOpen?.(false)}
                      />
                    ))}
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          ))}
        </div>
      </ScrollArea>
    </div>
  );
};
